import { Label } from "components/label"
import { CollapsibleSection } from "../../../../../../components/collapsibleSection"
import Input from "components/input"
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { IEditOrgForm, SupportedLanguage } from "interfaces";
import { HttpError } from "@refinedev/core";
import { SwitchButton } from "components/switchButton";
import Dropzone from "components/dropzone";
import Textarea from "components/textarea";
import ColorPicker from "components/colorPicker";
import { CONTENT_LANGUAGES } from "utills/constants";
import Button from "components/button";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { get } from "lodash";
import { TextEditor } from "components/textEditor";
import { useState } from "react";

const MAXIMUM_MENU_NUMBER = 3;

export const WhitelabelSection = (props: UseFormReturn<IEditOrgForm, HttpError, IEditOrgForm>) => {
  const [selectedLeftWebsiteFooterContentLanguage, setSelectedLeftWebsiteFooterContentLanguage] = useState(SupportedLanguage.EN);
  const [selectedRightWebsiteFooterContentLanguage, setSelectedRightWebsiteFooterContentLanguage] = useState(SupportedLanguage.EN);

  const { register, watch, control, formState: { errors }} = props;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "whitelabel_menu"
  });
  
  return (
    <CollapsibleSection 
      label="3rd party integration - Whitelabel"
      hideArrow={!watch("use_whitelabel_integration")}
      hideContent={!watch("use_whitelabel_integration")}
      isAnimated={false}
      customSubLabel={(
        <div className="mt-3">
          <Controller
            control={control}
            name="use_whitelabel_integration"
            defaultValue={false}
            render={({ field: { onChange, value = false, ref } }) => (
              <div>
                <SwitchButton 
                  checked={value}
                  onChange={onChange}
                  label="Use Whitelabel integration"
                  hint="Used to integrate the ticket purchasing flow with partner websites"
                />
              </div>
            )}
          />
          {errors["use_whitelabel_integration"] ? ( 
            <p className="error-text">
              <span className="font-medium">
                {errors["use_whitelabel_integration"]?.message}
              </span>
            </p> 
          ): null}
        </div>
      )}
    >
      <div className="divide-y divide-gray-100">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="flex flex-col gap-y-6 sm:col-span-3">
              <div>
                <Label 
                  id="whitelabel_domain" 
                  name="Sub-domain integration"
                  required
                />
                <Input 
                  id="whitelabel_domain"
                  type="text"
                  placeholder="Sub-domain integration"
                  error={errors["whitelabel_domain"]}
                  {...register("whitelabel_domain")}
                />
                {errors["whitelabel_domain"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["whitelabel_domain"].message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div>
                <Label 
                  id="whitelabel_brand_logo_url" 
                  name="Brand logo URL"  
                />
                <Input 
                  id="whitelabel_brand_logo_url"
                  type="text"
                  placeholder="Brand logo URL"
                  error={errors["whitelabel_brand_logo_url"]}
                  {...register("whitelabel_brand_logo_url")}
                />
                {errors["whitelabel_brand_logo_url"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["whitelabel_brand_logo_url"].message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="whitelabel_favicon" 
                name="Favicon"
                hint="Configure favicon for the whitelabel flow. Only ICO file formats are supported."
              />
              <Controller
                control={control}
                name="whitelabel_favicon"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="whitelabel_favicon"
                      customAccept={{
                        "image/file": [".ico"],
                      }}
                      customInvalidTypeMessage={"Only ICO file formats are supported."}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["whitelabel_favicon"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_favicon"].message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="col-span-full">
              <Label id="whitelabel_email_footer" name="Email footer" />
              <Textarea
                id="whitelabel_email_footer"
                placeholder="Email footer"
                error={errors["whitelabel_email_footer"]}
                {...register("whitelabel_email_footer")}
              />
              {errors["whitelabel_email_footer"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_email_footer"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <Label 
                id="whitelabel_sender_email"
                name="Sender email"
              />
              <Input 
                id="whitelabel_sender_email"
                type="text"
                placeholder="Sender email"
                error={errors["whitelabel_sender_email"]}
                {...register("whitelabel_sender_email")}
              />
              {errors["whitelabel_sender_email"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_sender_email"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="sm:col-span-2">
              <Label 
                id="whitelabel_email_footer_text_color" 
                name="Email footer text color"
              />
              <Controller
                control={control}
                name="whitelabel_email_footer_text_color"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <div className="relative">
                      <ColorPicker value={value} onChange={onChange} placeholder="Email footer text color" />
                    </div>
                  )
                }}
              />
              {errors["whitelabel_email_footer_text_color"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_email_footer_text_color"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="sm:col-span-2">
              <Label 
                id="whitelabel_header_text_color" 
                name="Header text color"
              />
              <Controller
                control={control}
                name="whitelabel_header_text_color"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <div className="relative">
                      <ColorPicker value={value} onChange={onChange} placeholder="Header text color" />
                    </div>
                  )
                }}
              />
              {errors["whitelabel_header_text_color"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_header_text_color"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="whitelabel_footer_color" 
                name="Footer color"
              />
              <Controller
                control={control}
                name="whitelabel_footer_color"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <div className="relative">
                      <ColorPicker value={value} onChange={onChange} placeholder="Footer color" />
                    </div>
                  )
                }}
              />
              {errors["whitelabel_footer_color"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_footer_color"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="whitelabel_footer_text_color" 
                name="Footer text color"
              />
              <Controller
                control={control}
                name="whitelabel_footer_text_color"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <div className="relative">
                      <ColorPicker value={value} onChange={onChange} placeholder="Footer text color" />
                    </div>
                  )
                }}
              />
              {errors["whitelabel_footer_text_color"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["whitelabel_footer_text_color"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="sm:col-span-3">
              <Controller
                control={control}
                name="use_whitelabel_signin_signup"
                defaultValue={true}
                render={({ field: { onChange, value = true, ref } }) => (
                  <Input
                    type="checkbox"
                    label="Use Sign in/Signup - Whitelabel"
                    id="use_whitelabel_signin_signup"
                    checked={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                      onChange(e.target.checked)
                    }    
                  />
                )}
              />
              {errors["use_whitelabel_signin_signup"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["use_whitelabel_signin_signup"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="sm:col-span-3">
              <Controller
                control={control}
                name="use_whitelabel_orders"
                defaultValue={true}
                render={({ field: { onChange, value = true, ref } }) => (
                  <Input
                    type="checkbox"
                    label="Use Orders - Whitelabel"
                    id="use_whitelabel_orders"
                    checked={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                      onChange(e.target.checked)
                    }    
                  />
                )}
              />
              {errors["use_whitelabel_orders"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["use_whitelabel_orders"].message}
                  </span>
                </p> 
              ): null}
            </div>
            <div className="col-span-full">
              <Label id="whitelabel_menu" name="Menu" />
              <div>
                {fields.length < MAXIMUM_MENU_NUMBER ? (
                  <div className="col-span-full">
                    <Button
                      type="button"
                      className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                      onClick={() => {
                        append({ url: "", name_en: "", name_vi: "" });
                      }}
                    >
                      <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                      <span className="shrink-0 text-sm">Add more</span>
                    </Button>
                  </div>
                ) : <></>}
                <div>
                  {fields.map((field, index: number) => (
                    <div key={field.id} className="mt-4 sm:mt-6">
                      <div className="flex justify-between items-center">
                        <Label
                          id={`whitelabel_menu`}
                          name={`Menu ${index + 1}`}
                        />
                        <button onClick={() => remove(index)} className="mb-2">
                          <TrashIcon className="w-5 h-5 text-brand-500" />
                        </button>
                      </div>
                      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-6">
                        {CONTENT_LANGUAGES.map((lang) => (
                          <div key={lang.value} className="sm:col-span-2">
                            <div className="relative">
                              <Input 
                                id={`whitelabel_menu.${index}`}
                                type="text"
                                placeholder="Menu name"
                                className="pr-11"
                                error={get(errors, `whitelabel_menu.${index}.name_${lang.value}`,"")}
                                {...register(`whitelabel_menu.${index}.name_${lang.value}`)}
                              />
                              <img
                                src={lang.icon}
                                alt="Language Icon"
                                className="absolute top-1/2 right-3 -translate-y-1/2"
                              />
                            </div>
                            {get(errors, `whitelabel_menu.${index}.name_${lang.value}`,"") ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {get(errors, `whitelabel_menu.${index}.name_${lang.value}.message`, "")}
                                </span>
                              </p>
                            ) : null}
                          </div>
                        ))}
                        <div className="sm:col-span-2">
                          <Input 
                            d={`whitelabel_menu.${index}.url`}
                            type="text"
                            placeholder="URL"
                            error={get(errors, `whitelabel_menu.${index}.url`,"")}
                            {...register(`whitelabel_menu.${index}.url`)}
                          />
                          {get(errors, `whitelabel_menu.${index}.url`,"") ? ( 
                            <p className="error-text">
                              <span className="font-medium">
                                {get(errors, `whitelabel_menu.${index}.url.message`, "")}
                              </span>
                            </p> 
                          ): null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-span-full">
              <Label id="whitelabel_left_website_footer_content" name="Left Website Footer content" />
              <div>
                {CONTENT_LANGUAGES.map(lang => (
                  <button 
                    className={`px-4 py-2 rounded-tl-md rounded-tr-md ${errors[`whitelabel_left_website_footer_content_${lang.value}`] ? "border-red-500" : "border-magnolia-600"} border-solid border-[1px] ${selectedLeftWebsiteFooterContentLanguage === lang.value ? "bg-gray-300" : "bg-white"}`} 
                    onClick={() => setSelectedLeftWebsiteFooterContentLanguage(lang.value)}
                    type="button"
                    key={lang.value}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
              <div className={`w-full ${selectedLeftWebsiteFooterContentLanguage === SupportedLanguage.VI ? "block" : "hidden"}`}>
                <Controller
                  control={control}
                  name="whitelabel_left_website_footer_content_vi"
                  render={({ field: { onChange, value = "" } }) => (
                    <div key="whitelabel_left_website_footer_content_vi">
                      <TextEditor
                        value={value}
                        onChange={onChange}
                        toolbarSticky={true}
                        key="whitelabel_left_website_footer_content_vi"
                      />
                    </div>
                  )}
                />
                {errors["whitelabel_left_website_footer_content_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.whitelabel_left_website_footer_content_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className={`w-full ${selectedLeftWebsiteFooterContentLanguage === SupportedLanguage.EN ? "block" : "hidden"}`}>
                <Controller
                  control={control}
                  name="whitelabel_left_website_footer_content_en"
                  render={({ field: { onChange, value = "" } }) => (
                    <div key="whitelabel_left_website_footer_content_en">
                      <TextEditor
                        value={value}
                        onChange={onChange}
                        toolbarSticky={true}
                        key="whitelabel_left_website_footer_content_en"
                      />
                    </div>
                  )}
                />
                {errors["whitelabel_left_website_footer_content_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.whitelabel_left_website_footer_content_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
            <div className="col-span-full">
              <Label id="whitelabel_right_website_footer_content" name="Right Website Footer content" />
              <div>
                {CONTENT_LANGUAGES.map(lang => (
                  <button 
                    className={`px-4 py-2 rounded-tl-md rounded-tr-md ${errors[`whitelabel_right_website_footer_content_${lang.value}`] ? "border-red-500" : "border-magnolia-600"} border-solid border-[1px] ${selectedRightWebsiteFooterContentLanguage === lang.value ? "bg-gray-300" : "bg-white"}`} 
                    onClick={() => setSelectedRightWebsiteFooterContentLanguage(lang.value)}
                    type="button"
                    key={lang.value}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
              <div className={`w-full ${selectedRightWebsiteFooterContentLanguage === SupportedLanguage.VI ? "block" : "hidden"}`}>
                <Controller
                  control={control}
                  name="whitelabel_right_website_footer_content_vi"
                  render={({ field: { onChange, value = "" } }) => (
                    <div key="whitelabel_right_website_footer_content_vi">
                      <TextEditor
                        value={value}
                        onChange={onChange}
                        toolbarSticky={true}
                        key="whitelabel_right_website_footer_content_vi"
                      />
                    </div>
                  )}
                />
                {errors["whitelabel_right_website_footer_content_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.whitelabel_right_website_footer_content_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className={`w-full ${selectedRightWebsiteFooterContentLanguage === SupportedLanguage.EN ? "block" : "hidden"}`}>
                <Controller
                  control={control}
                  name="whitelabel_right_website_footer_content_en"
                  render={({ field: { onChange, value = "" } }) => (
                    <div key="whitelabel_right_website_footer_content_en">
                      <TextEditor
                        value={value}
                        onChange={onChange}
                        toolbarSticky={true}
                        key="whitelabel_right_website_footer_content_en"
                      />
                    </div>
                  )}
                />
                {errors["whitelabel_right_website_footer_content_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.whitelabel_right_website_footer_content_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>

          </div>
        </div>
      </div>
    </CollapsibleSection>
  )
}